import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import useIsDesktop from '../../hooks/useIsDesktop';
import EmptyState from './EmptyState';

/**
 * columns: [{ key, label, render?(row) }]
 * rows: array of data objects
 * rowKey: (row) => string | number
 * actions?: (row) => [{ label, onClick, danger? }]  — renders a row menu
 */
function DataTable({
  columns,
  rows,
  rowKey,
  loading,
  emptyIcon,
  emptyTitle = 'Nothing here yet',
  emptyMessage,
  actions,
  pageSize = 10,
}) {
  const isDesktop = useIsDesktop();
  const [page, setPage] = useState(0);
  const [openMenuKey, setOpenMenuKey] = useState(null);

  if (loading) {
    return (
      <div style={styles.skeletonWrap}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={styles.skeletonRow} className="skeleton-pulse" />
        ))}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} message={emptyMessage} />;
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  if (!isDesktop) {
    return (
      <div>
        <div style={styles.cardList}>
          {pageRows.map((row) => {
            const key = rowKey(row);
            return (
              <div key={key} style={styles.mobileCard}>
                {columns.map((col) => (
                  <div key={col.key} style={styles.mobileField}>
                    <span style={styles.mobileLabel}>{col.label}</span>
                    <span style={styles.mobileValue}>{col.render ? col.render(row) : row[col.key]}</span>
                  </div>
                ))}
                {actions && (
                  <div style={styles.mobileActions}>
                    {actions(row).map((a) => (
                      <button
                        key={a.label}
                        onClick={a.onClick}
                        style={{
                          ...styles.mobileActionBtn,
                          color: a.danger ? 'var(--color-danger)' : 'var(--color-primary)',
                        }}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {totalPages > 1 && <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />}
      </div>
    );
  }

  return (
    <div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={styles.th}>{col.label}</th>
              ))}
              {actions && <th style={styles.th} />}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const key = rowKey(row);
              return (
                <tr key={key} className="data-row">
                  {columns.map((col) => (
                    <td key={col.key} style={styles.td}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                  {actions && (
                    <td style={{ ...styles.td, position: 'relative', textAlign: 'right' }}>
                      <button
                        onClick={() => setOpenMenuKey(openMenuKey === key ? null : key)}
                        style={styles.menuBtn}
                        aria-label="Row actions"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuKey === key && (
                        <div style={styles.menu} onMouseLeave={() => setOpenMenuKey(null)}>
                          {actions(row).map((a) => (
                            <button
                              key={a.label}
                              onClick={() => {
                                setOpenMenuKey(null);
                                a.onClick();
                              }}
                              style={{
                                ...styles.menuItem,
                                color: a.danger ? 'var(--color-danger)' : 'var(--color-ink)',
                              }}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  return (
    <div style={styles.pagination}>
      <button
        onClick={() => onChange(Math.max(0, page - 1))}
        disabled={page === 0}
        style={{ ...styles.pageBtn, opacity: page === 0 ? 0.5 : 1 }}
      >
        Prev
      </button>
      <span style={styles.pageInfo}>{page + 1} / {totalPages}</span>
      <button
        onClick={() => onChange(Math.min(totalPages - 1, page + 1))}
        disabled={page === totalPages - 1}
        style={{ ...styles.pageBtn, opacity: page === totalPages - 1 ? 0.5 : 1 }}
      >
        Next
      </button>
    </div>
  );
}

const styles = {
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-ink-soft)',
    borderBottom: '1px solid var(--color-border)',
    position: 'sticky',
    top: 0,
    backgroundColor: 'var(--color-surface)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--color-border-soft)',
    color: 'var(--color-ink)',
    verticalAlign: 'middle',
  },
  menuBtn: {
    width: '30px',
    height: '30px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-ink-soft)',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  menu: {
    position: 'absolute',
    right: 0,
    top: '100%',
    zIndex: 10,
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '10px',
    boxShadow: 'var(--shadow-md)',
    minWidth: '150px',
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
  },
  menuItem: {
    padding: '8px 10px',
    fontSize: '13px',
    fontWeight: 600,
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  mobileCard: {
    border: '1px solid var(--color-border)',
    borderRadius: '10px',
    padding: '14px 16px',
  },
  mobileField: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '6px 0',
    borderBottom: '1px solid var(--color-border-soft)',
  },
  mobileLabel: {
    fontSize: '12px',
    color: 'var(--color-ink-soft)',
    fontWeight: 600,
  },
  mobileValue: {
    fontSize: '13px',
    color: 'var(--color-ink)',
    textAlign: 'right',
  },
  mobileActions: {
    display: 'flex',
    gap: '14px',
    marginTop: '10px',
  },
  mobileActionBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
  },
  skeletonWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  skeletonRow: {
    height: '44px',
    borderRadius: '8px',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
    marginTop: '16px',
  },
  pageBtn: {
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '13px',
    color: 'var(--color-ink-soft)',
    fontVariantNumeric: 'tabular-nums',
  },
};

export default DataTable;
